import { combineReducers } from "@reduxjs/toolkit";
import drinkSlice from "../slices/drinkSlice";
import outletSlice from "../slices/outletSlice";
import filtersSlice from "../slices/filtersSlice";
import skuSlice from "../slices/skuSlice";
import authSlice, { logout, impersonateUser, exitImpersonation } from "../slices/authSlice";
import savedListSlice from "../slices/savedListSlice";
import savedFilterViewsReducer from "../slices/savedFilterViewsSlice";
import marketOverviewSlice from "../slices/marketOverviewSlice";
import marketOverviewFiltersSlice from "../slices/marketOverviewFiltersSlice";
import marketDeepdiveSlice from "../slices/marketDeepdiveSlice";
import keyAccountsSlice from "../slices/keyAccountsSlice";
import competitorsSlice from "../slices/competitorsSlice";
import workspaceSlice from "../slices/workspaceSlice";
import internalToolSlice from "../slices/internalToolSlice";
import physicalViewSlice from "../slices/physicalViewSlice";
import settingsSlice from "../slices/settingsSlice";
import filterDimensionsReducer from "../slices/filterDimensionsSlice";
import physicalFilterDimensionsReducer from "../slices/physicalFilterDimensionsSlice";
import userPreferencesReducer from "../slices/userPreferencesSlice";
import personaConfigReducer from "../slices/personaConfigSlice";
import workspaceConfigReducer from "../slices/workspaceConfigSlice";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { clearStaffPersonaSession } from "../utils/staffPersonaSession";

/**
 * Strip stale request-state flags and server-fetched country data from a
 * persisted auth blob.
 *
 * Existing users may have `loading: true` (or similar) frozen in
 * localStorage from a prior session where an auth thunk was in flight
 * at unload, or a country list the API has since changed. The blacklist on
 * `authPersistConfig` prevents future saves from carrying those fields, but
 * rehydrate still loads whatever the old blob has — so a one-shot migration
 * here clears them. Keeping the migration permanently is safe: post-fix saves
 * won't include these fields, so the inputs to this function will already be
 * clean.
 *
 * Exported for unit testing — never call directly.
 */
export const stripStaleAuthRequestState = async (state) =>
  state
    ? {
        ...state,
        loading: false,
        error: null,
        userDataFetchError: null,
        countries: [],
        defaultCountry: null,
        countriesRequest: null,
        status: "resolving_scope",
      }
    : state;

const outletPersistConfig = {
  key: "outlet",
  storage: storage,
  blacklist: [
    "firstLoading",
    "outlets", // Large data - don't persist
    "originalOutlets", // Large data - don't persist
    "petPlatformData", // Large data - don't persist
    "pageInfo", // Derived from API - don't persist so core_brands always re-fetched
    "loading",
    "error",
  ],
};

const filtersPersistConfig = {
  key: "filters",
  storage: storage,
  // filterTextOptions holds server-fetched brand/category trees — re-fetched on
  // load, so don't persist them (keeps localStorage small). appliedFilters /
  // appliedSorters (user selections) still persist.
  // brandTreeScope and subbrandVariants describe those trees and their
  // requests, so they follow the trees.
  blacklist: [
    "firstLoading",
    "loading",
    "error",
    "filterTextOptions",
    "deepLinkCountry",
    "brandTreeScope",
    "subbrandVariants",
  ],
};

const skuPersistConfig = {
  key: "sku",
  storage: storage,
  // `loading` and `errors` are nested per-dimension request-state objects
  // (companies/brands/subbrands/...). Persisting them would freeze a stale
  // pending state across sessions; a request that was in flight at unload
  // would rehydrate as still-loading and never flip back.
  // `filteredBeverages` (the selected SKU tabs) is intentionally persisted so it
  // stays consistent with the persisted `activeTabKey` — otherwise a reload
  // rehydrates an active SKU tab with no beverage behind it and the grid falls
  // back to the default performance metrics.
  blacklist: ["firstLoading", "loading", "errors"],
};

export const authPersistConfig = {
  key: "auth",
  storage: storage,
  // `loading` and `error` are request-state flags. If an auth thunk
  // (fetchClientCountries, switchWorkspaceAndUpdateCountry, ...) was
  // pending when the tab unloaded, persisting `loading: true` left it
  // stuck on the next session — components gated on auth.loading
  // (e.g. Physical Location View header) showed skeletons forever.
  //
  // The impersonation flags (isImpersonating / impersonatedUserEmail /
  // impersonationSessionId / impersonationExpiresAt) ARE persisted on purpose:
  // Firebase keeps the impersonated identity across a page reload, so Redux must
  // keep the banner + session id too — otherwise the admin reloads into the
  // target's account with no Exit button and no way back. None of these
  // are credentials; the admin restore token is minted on demand at exit, never
  // stored client-side.
  //
  // `availableWorkspaces` is the server's `available_workspaces`, read fresh
  // from `/users` every session. It is NOT persisted: a persisted list would
  // outlive a server-side revocation until the next logout. The boot gate
  // holds rendering until `/users` answers, so there is no empty switcher to
  // flash. `workspaceListError` belongs to that same read.
  // `countries` and `defaultCountry` come from the API on every load, and that
  // fetch is where filtersSlice selects the country, so neither is persisted.
  // `countriesRequest` describes a fetch in flight, which never outlives the tab.
  // `status` restarts at "resolving_scope" on every load for the same reason.
  blacklist: [
    "loading",
    "error",
    "userDataFetchedThisSession",
    "userDataFetchError",
    "availableWorkspaces",
    "workspaceListError",
    "countries",
    "defaultCountry",
    "countriesRequest",
    "status",
  ],
  // Strip stale `loading: true` etc. from existing localStorage blobs —
  // see stripStaleAuthRequestState for the why.
  migrate: stripStaleAuthRequestState,
};

const savedListPersistConfig = {
  key: "savedList",
  storage: storage,
  blacklist: [
    "firstLoading",
    "loading",
    "tableLoading",
    "refreshLoading",
    "summaryLoading",
    "error",
    // Derived API responses — re-fetched when a list is opened. Don't persist.
    "currentListTableData",
    "currentListRefreshData",
    "currentListSummary",
  ],
};

const marketOverviewPersistConfig = {
  key: "marketOverview",
  storage: storage,
  blacklist: [
    "loading",
    "error",
    "apiData", // Large data - don't persist
    "geoscopeData", // Large data - don't persist
    "geoscopeLoading",
    "geoscopeError",
    "latestRequestId",
  ],
};

const marketOverviewFiltersPersistConfig = {
  key: "marketOverviewFilters",
  storage: storage,
  blacklist: ["platformsLoading", "franchisesLoading", "error"],
};

// Only the grain and the range live here, so nothing is blacklisted.
const marketDeepdivePersistConfig = {
  key: "marketDeepdive",
  storage: storage,
};

// The two Key Accounts grains and the workspace they belong to; nothing to blacklist.
const keyAccountsPersistConfig = {
  key: "keyAccounts",
  storage: storage,
};

const competitorsPersistConfig = {
  key: "competitors",
  storage: storage,
  blacklist: ["loading", "error"],
};

const workspacePersistConfig = {
  key: "workspace",
  storage: storage,
  blacklist: ["loading", "error", "fetchingWorkspaces", "fetchWorkspacesError", "success"],
};

const physicalViewPersistConfig = {
  key: "physicalView",
  storage: storage,
  blacklist: [
    "cardsLoading",
    "tableLoading",
    "dimensionsLoading",
    "isDataLoading",
    "cardsError",
    "tableError",
    "dimensionsError",
    "firstLoading",
    "tableData", // Large data - don't persist
    "latestCardsRequestId",
    "latestTableRequestId",
  ],
};

const settingsPersistConfig = {
  key: "settings",
  storage: storage,
};

// Every persisted slice, keyed by its name in the store. `appReducer` is built
// from this object and the storage-key list is derived from it, so the two
// cannot disagree: a slice missing from here is not persisted at all, rather
// than persisted but never cleared on logout. A hand-written list of key
// strings, by contrast, can half-clear state by naming a key nothing persists
// or omitting one that something does.
//
// Each entry's own `config.key` must match its property name, which
// `rootReducer.test.js` asserts. redux-persist reads the config's key and the
// store reads the property, so a mismatch would persist one slice under
// another's storage key.
// Exported for unit testing.
export const SLICE_PERSIST_CONFIGS = {
  outlet: { config: outletPersistConfig, reducer: outletSlice },
  filters: { config: filtersPersistConfig, reducer: filtersSlice },
  sku: { config: skuPersistConfig, reducer: skuSlice },
  auth: { config: authPersistConfig, reducer: authSlice },
  savedList: { config: savedListPersistConfig, reducer: savedListSlice },
  marketOverview: {
    config: marketOverviewPersistConfig,
    reducer: marketOverviewSlice,
  },
  marketOverviewFilters: {
    config: marketOverviewFiltersPersistConfig,
    reducer: marketOverviewFiltersSlice,
  },
  marketDeepdive: {
    config: marketDeepdivePersistConfig,
    reducer: marketDeepdiveSlice,
  },
  keyAccounts: { config: keyAccountsPersistConfig, reducer: keyAccountsSlice },
  competitors: { config: competitorsPersistConfig, reducer: competitorsSlice },
  workspace: { config: workspacePersistConfig, reducer: workspaceSlice },
  physicalView: { config: physicalViewPersistConfig, reducer: physicalViewSlice },
  settings: { config: settingsPersistConfig, reducer: settingsSlice },
};

// Surface a write that failed, which in practice means localStorage is full.
// redux-persist calls this per slice and otherwise swallows the error: its own
// fallback logs only when NODE_ENV is not production, which a Vite build
// compiles out. Without a handler the quota the per-slice blacklists exist to
// stay under would be exceeded silently, in exactly the builds users run.
export const logPersistWriteFailure = (key) => (err) => {
  console.warn(`[redux-persist] write failed for persist:${key}:`, err);
};

/**
 * Wrap each slice's reducer in its persistReducer, keeping the keys.
 *
 * The mapped return type is what gives `combineReducers` a statically-known key
 * set, and through it `RootState` in store/types.ts. A plain
 * `Object.fromEntries` types the result as an index signature instead, so every
 * persisted slice drops out of `RootState` and `state.auth` stops type-checking
 * in a .ts consumer.
 *
 * Every slice gets the write-fail handler here rather than in its own config,
 * so a new slice cannot be added without one.
 *
 * @template {Record<string, {config: object, reducer: unknown}>} T
 * @param {T} slices
 * @returns {{ [K in keyof T]: import("redux").Reducer }}
 */
const persistEach = (slices) =>
  /** @type {any} */ (
    Object.fromEntries(
      Object.entries(slices).map(([key, { config, reducer }]) => [
        key,
        persistReducer({ ...config, writeFailHandler: logPersistWriteFailure(key) }, reducer),
      ])
    )
  );

// Single source of truth for the persisted storage keys, used by the logout
// handler below to clear everything. Derived rather than written out, so a key
// here always names a slice something actually persists.
//
// `personaConfig` and `userPreferences` are absent because neither is written
// to storage at all: Firestore owns both and every load reads them back. A
// local copy would be a second source of truth that rehydrates ahead of the
// fetch, so it wins on disagreement and hides a broken read.
// Exported for unit testing.
export const PERSISTED_STORAGE_KEYS = Object.keys(SLICE_PERSIST_CONFIGS).map(
  (key) => `persist:${key}`
);

// Keys no build writes any more, cleared once at startup rather than on logout.
// A logout-only purge never reaches a user who simply closes the tab, and
// localStorage has no expiry, so the blob would outlive everyone who wrote it.
// Retire an entry from this list once no browser can still be carrying it.
// Exported for unit testing.
export const RETIRED_STORAGE_KEYS = [
  // The persona config's storage key from before the server owned it.
  "persist:roleConfig",
  // Column widths, favorites, pinned workspaces and focus mode are all
  // authoritative in Firestore, so a local copy can only disagree with them.
  "persist:userPreferences",
  // An always-empty blob: the drink config blacklisted its slice's whole state.
  "persist:drink",
  // A whole-tree copy written by a root persistReducer, which the per-slice
  // configs replace.
  "persist:root",
  // The Firebase ID token cache. The token lives only in memory and in
  // Firebase's own IndexedDB session now, so this plaintext copy at a guessable
  // key is retired and cleared from browsers that still hold one.
  "auth_state",
];

// Retired key families that a build wrote once per workspace, so the purge
// matches them by prefix rather than by name.
export const RETIRED_STORAGE_PREFIXES = [
  // The navigation shell each workspace last rendered. Each key names a
  // workspace the session opened, and the shell now comes from the
  // workspace's UI config.
  "dashone-top-bar:",
];

// filterDimensions: never persist — re-fetched on first dashboard-route mount
// (GeneralLayout), not on the DashAI chat routes that don't consume it
const appReducer = combineReducers({
  ...persistEach(SLICE_PERSIST_CONFIGS),
  drink: drinkSlice,
  personaConfig: personaConfigReducer,
  userPreferences: userPreferencesReducer,
  internalTool: internalToolSlice,
  filterDimensions: filterDimensionsReducer,
  physicalFilterDimensions: physicalFilterDimensionsReducer,
  savedFilterViews: savedFilterViewsReducer,
  workspaceConfig: workspaceConfigReducer,
});

// Entering or leaving a "View as User" session is an identity swap: every
// workspace-scoped slice — country, filter dimensions (incl. the id_client the
// dashboard-data requests read from), SKU selection, saved views, column prefs —
// must reset so it re-derives for the new identity. Otherwise the impersonator's
// context bleeds into the target's session (empty dashboards, wrong id_client,
// spurious 403s). These are the thunk action types that run
// setImpersonationActive / clearImpersonation.
const IMPERSONATION_TRANSITIONS = new Set([
  impersonateUser.fulfilled.type,
  exitImpersonation.fulfilled.type,
  exitImpersonation.rejected.type,
]);

export const rootReducer = (state, action) => {
  if (action.type === logout.fulfilled.type) {
    try {
      PERSISTED_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
    } catch (error) {
      console.warn("Failed to clear localStorage:", error);
    }
    // A Dashmote staff member's onboarding answers end here, which is what
    // returns them to the Power User default they hold in every workspace.
    clearStaffPersonaSession();

    // Reset to initial state by passing undefined
    // This allows each reducer to use its initial state
    // combineReducers will handle undefined state correctly.
    // personaConfig resets with everything else: the server owns it, so the
    // next load reads it back rather than needing it grafted through.
    return appReducer(undefined, action);
  }

  if (IMPERSONATION_TRANSITIONS.has(action.type)) {
    // Same initial-state reset as logout, but the auth slice must be PRESERVED:
    // it carries the transition itself (target identity + impersonation flags),
    // which its own reducer has just applied. So reset every slice to initial,
    // then graft the transitioned auth back on. No manual localStorage purge —
    // there is no reload at the transition, and redux-persist re-writes the
    // reset slices as initial on the next tick.
    // personaConfig is grafted too, and here it must be: no fetch runs at the
    // transition, so resetting it would leave the admin with a config that is
    // neither loaded nor loading and the gate waiting on a read that never
    // comes. Grafting keeps the admin's own configs (and the loaded status)
    // across the round-trip, while the target's uid finds nothing in them and
    // resolves to the everything-view — which is the intended read-only lens.
    const transitioned = appReducer(state, action);
    const cleared = appReducer(undefined, action);
    return {
      ...cleared,
      auth: transitioned.auth,
      personaConfig: transitioned.personaConfig,
    };
  }

  return appReducer(state, action);
};
